import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { AltSummOverrideProvider } from 'contexts/altsumm-override/AltSummOverrideContext';
import { GetAllessehSnsUpdatesSubDocument, RefreshContentSearch } from 'data/generated/graphql';
import { getAllessehContentByOriginId } from 'hooks/useAllessehContentByOriginIdQuery';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { useAuthToken } from 'hooks/useAuthToken';
import { useConvertedProperty } from 'hooks/useConvertedProperty';
import { useSubscription } from 'hooks/useSubscription';
import { reportError } from 'utils/errorBoundary';

export type AllessehContentUpdateCallback = (content: AllessehContent) => void;
export type AllessehContentUpdateEventCallback = (event: RefreshContentSearch) => void;

interface AllessehContentUpdateContextType {
  // Register a callback to receive the refreshed AllessehContent for a specific originId.
  // Pass initialContent to seed the hash baseline so the first SNS fetch is compared against
  // known content rather than treated as an unconditional change.
  subscribe: (originId: string, cb: AllessehContentUpdateCallback, initialContent?: AllessehContent) => void;
  unsubscribe: (originId: string, cb: AllessehContentUpdateCallback) => void;
  // Register a callback to receive every raw subscription event regardless of originId.
  // Useful for triggering broader refreshes (e.g. re-fetching a search results list).
  subscribeToEvents: (cb: AllessehContentUpdateEventCallback) => void;
  unsubscribeFromEvents: (cb: AllessehContentUpdateEventCallback) => void;
  // Returns the most recently fetched AllessehContent for an originId, or null if none yet.
  // Used to seed freshly-mounted components so reordering doesn't lose SNS-updated content.
  getLatestContent: (originId: string) => AllessehContent | null;
}

const AllessehContentUpdateContext = createContext<AllessehContentUpdateContextType>({
  subscribe: () => {},
  unsubscribe: () => {},
  subscribeToEvents: () => {},
  unsubscribeFromEvents: () => {},
  getLatestContent: () => null
});

// Delays (ms) between each successive fetch attempt after an SNS notification.
// Attempt 1 fires after BACKOFF_DELAYS_MS[0], attempt 2 after BACKOFF_DELAYS_MS[1], etc.
// The chain stops as soon as the content hash changes or all attempts are exhausted.
export const BACKOFF_DELAYS_MS = [100, 1_000, 3_000, 6_000, 10_000];
export const MAX_BACKOFF_ATTEMPTS = BACKOFF_DELAYS_MS.length;

export const AllessehContentUpdateProvider = ({ children }: { children: React.ReactNode }) => {
  const authToken = useAuthToken();
  const currentProperty = useConvertedProperty();

  // Stored in refs so mutations never trigger a re-render of the provider or its consumers.
  const subscribersRef = useRef<Map<string, Set<AllessehContentUpdateCallback>>>(new Map());
  const eventSubscribersRef = useRef<Set<AllessehContentUpdateEventCallback>>(new Set());
  const latestContentRef = useRef<Map<string, AllessehContent>>(new Map());
  const contentHashRef = useRef<Map<string, string>>(new Map());

  // Keep latest authToken in a ref so timer callbacks always read current value.
  const authTokenRef = useRef(authToken);
  authTokenRef.current = authToken;

  const subscribePayload = useMemo(() => ({ query: GetAllessehSnsUpdatesSubDocument }), []);
  const { data } = useSubscription({
    subscribePayload,
    publicationKey: currentProperty ?? '',
    enabled: !!currentProperty
  });

  useEffect(() => {
    const event = data?.refreshContentSearch;
    if (!event) return;

    const { originId } = event;

    const tryFetch = (attempt: number) => {
      setTimeout(async () => {
        // Attempt 1 only: notify global event subscribers (e.g. search list refresh).
        if (attempt === 1) {
          eventSubscribersRef.current.forEach((cb) => cb(event));
        }

        const token = authTokenRef.current;
        if (!token || !subscribersRef.current.has(originId)) return;

        try {
          const content = await getAllessehContentByOriginId(token, originId);

          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (!content) {
            if (attempt < MAX_BACKOFF_ATTEMPTS) tryFetch(attempt + 1);
            return;
          }

          const newHash = JSON.stringify(content);
          const previousHash = contentHashRef.current.get(originId);

          if (newHash !== previousHash) {
            contentHashRef.current.set(originId, newHash);
            latestContentRef.current.set(originId, content);
            subscribersRef.current.get(originId)?.forEach((cb) => cb(content));
            return;
          }

          if (attempt < MAX_BACKOFF_ATTEMPTS) tryFetch(attempt + 1);
        } catch (err) {
          reportError(
            `[AllessehContentUpdate] Failed to fetch updated content for originId: ${originId}`,
            err as Error
          );
        }
      }, BACKOFF_DELAYS_MS[attempt - 1]);
    };

    tryFetch(1);
  }, [data]);

  const subscribe = useCallback(
    (originId: string, cb: AllessehContentUpdateCallback, initialContent?: AllessehContent) => {
      if (!subscribersRef.current.has(originId)) {
        subscribersRef.current.set(originId, new Set());
      }
      subscribersRef.current.get(originId)!.add(cb);
      if (initialContent && !contentHashRef.current.has(originId)) {
        contentHashRef.current.set(originId, JSON.stringify(initialContent));
      }
    },
    []
  );

  const unsubscribe = useCallback((originId: string, cb: AllessehContentUpdateCallback) => {
    const subscribers = subscribersRef.current.get(originId);
    if (!subscribers) return;
    subscribers.delete(cb);
    if (subscribers.size === 0) {
      subscribersRef.current.delete(originId);
    }
  }, []);

  const getLatestContent = useCallback((originId: string) => latestContentRef.current.get(originId) ?? null, []);

  const subscribeToEvents = useCallback((cb: AllessehContentUpdateEventCallback) => {
    eventSubscribersRef.current.add(cb);
  }, []);

  const unsubscribeFromEvents = useCallback((cb: AllessehContentUpdateEventCallback) => {
    eventSubscribersRef.current.delete(cb);
  }, []);

  const value = useMemo(
    () => ({ subscribe, unsubscribe, subscribeToEvents, unsubscribeFromEvents, getLatestContent }),
    [subscribe, unsubscribe, subscribeToEvents, unsubscribeFromEvents, getLatestContent]
  );

  return (
    <AllessehContentUpdateContext.Provider value={value}>
      <AltSummOverrideProvider>{children}</AltSummOverrideProvider>
    </AllessehContentUpdateContext.Provider>
  );
};

export const useAllessehContentUpdateContext = () => useContext(AllessehContentUpdateContext);
